// src/pages/RefundPolicy.jsx — Broadway Holidays
// Add to App.js:
//   import RefundPolicy from "./pages/RefundPolicy";
//   <Route path="/refund-policy" element={<RefundPolicy />} />

import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const NAV  = "#0B1F3A";
const NAVL = "#1e3a5c";
const GRN  = "#059669";
const PJS  = { fontFamily: "'Plus Jakarta Sans',sans-serif" };
const DS   = { fontFamily: "'DM Sans',sans-serif" };

const SECTIONS = [
  {
    id: "overview",
    emoji: "📋",
    title: "Policy Overview",
    content: `Broadway Holidays is committed to providing transparent, fair, and customer-friendly cancellation and refund terms. This policy applies to all holiday bookings made directly through broadwayholidays.co.uk, by phone, WhatsApp, or email.

All bookings are subject to the specific terms of the hotel, airline, or supplier involved. We will always clearly communicate any supplier-specific restrictions at the time of booking.

This policy was last updated: May 2026.`,
  },
  {
    id: "deposits",
    emoji: "💳",
    title: "Deposits & Payments",
    content: `A non-refundable deposit is required to secure your booking. The deposit amount varies by booking but is typically 8–10% of the total holiday cost, with a minimum of £19 per person.

The remaining balance is due no later than 10 weeks (70 days) before your scheduled departure date. Failure to pay the balance by the due date may result in your booking being cancelled and the deposit being forfeited.

Full payment is required for bookings made within 10 weeks of departure.`,
  },
  {
    id: "cancellation",
    emoji: "❌",
    title: "Cancellation by You",
    content: `If you wish to cancel your booking, you must notify us in writing by email to support@broadwayholidays.co.uk. Cancellation charges are calculated from the date we receive your written notice.

The following cancellation charges apply:

• More than 70 days before departure — Deposit only (lost)
• 43–70 days before departure — 50% of total holiday cost
• 29–42 days before departure — 70% of total holiday cost
• 15–28 days before departure — 90% of total holiday cost
• 14 days or fewer before departure — 100% of total holiday cost (no refund)

These charges reflect costs we incur from hotels, airlines, and other suppliers that cannot be recovered once a booking is cancelled.`,
  },
  {
    id: "amendments",
    emoji: "✏️",
    title: "Amendments & Changes",
    content: `We understand that plans can change. We will do our best to accommodate amendments to your booking, subject to availability and supplier terms.

Amendment requests must be made in writing. An administration fee of £25 per person per change may apply, in addition to any costs charged by the hotel or airline.

Name changes, date changes, and destination changes are treated as cancellation and rebooking in most cases, and full cancellation charges may apply. We will always advise you of the full cost before processing any amendment.`,
  },
  {
    id: "cancellation-us",
    emoji: "🏢",
    title: "Cancellation by Broadway Holidays",
    content: `In the unlikely event that we need to cancel your booking, we will notify you as soon as possible and offer you one of the following options:

• A comparable alternative holiday at the same or lower price
• A full refund of all money paid to us within 14 days

We will not cancel your booking within 10 weeks of departure unless due to events beyond our control (force majeure — see below), or non-payment of your balance.

If we cancel due to our own fault (not force majeure), we will also pay you compensation depending on the circumstances.`,
  },
  {
    id: "force-majeure",
    emoji: "⚠️",
    title: "Force Majeure",
    content: `We cannot accept liability or pay compensation where the performance or prompt performance of our obligations is prevented or affected by circumstances amounting to "force majeure".

Force majeure means events such as: war, threat of war, civil strife, terrorist activity, natural or nuclear disaster, fire, adverse weather conditions, epidemic or pandemic, unavoidable technical problems with transport, closure of airports or ports, or similar events beyond our or our suppliers' control.

In such cases, we will endeavour to offer suitable alternatives or credit notes, but cash refunds cannot be guaranteed.`,
  },
  {
    id: "insurance",
    emoji: "🛡️",
    title: "Travel Insurance",
    content: `We strongly recommend that all customers take out comprehensive travel insurance at the time of booking. Travel insurance can cover you for cancellation charges, medical emergencies, lost luggage, and many other unforeseen circumstances.

Broadway Holidays cannot be held liable for losses that would have been covered by appropriate travel insurance. We are happy to recommend travel insurance providers — please ask our team when booking.

If you choose to travel without insurance, you do so entirely at your own risk.`,
  },
  {
    id: "refund-process",
    emoji: "💰",
    title: "Refund Process",
    content: `Where a refund is due, we will process it within 14 working days of the cancellation being confirmed and all required information being received.

Refunds will be made to the original payment method where possible. Card refunds may take an additional 3–5 business days to appear in your account depending on your bank.

If you paid by bank transfer, refunds will be made to the same account. Please ensure you provide your bank details when requesting a refund.

Partial refunds (e.g. for amendments or partial cancellations) will be calculated on a case-by-case basis.`,
  },
  {
    id: "complaints",
    emoji: "📞",
    title: "Complaints & Disputes",
    content: `If you are unhappy with any aspect of our service or the handling of your cancellation or refund, please contact us in the first instance:

Email: support@broadwayholidays.co.uk
Phone: 020 3019 0055
WhatsApp: 07487 672075

We aim to respond to all complaints within 2 business days and resolve them within 14 days.

If you remain dissatisfied, you may be entitled to refer your complaint to an alternative dispute resolution (ADR) scheme. Further details are available on request.`,
  },
];

export default function RefundPolicy() {
  const [active, setActive] = useState(null);

  return (
    <>
      <Navbar />

      {/* ── HERO ── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAV} 0%, ${NAVL} 60%, #2d5a8e 100%)`,
        padding: "60px 20px 50px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Background decoration */}
        <div style={{
          position: "absolute", top: -60, right: -60,
          width: 300, height: 300, borderRadius: "50%",
          background: "rgba(255,255,255,.03)", pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute", bottom: -40, left: "30%",
          width: 200, height: 200, borderRadius: "50%",
          background: "rgba(255,255,255,.02)", pointerEvents: "none"
        }} />

        <div style={{ maxWidth: 860, margin: "0 auto", position: "relative" }}>
          <nav style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 20, fontSize: 12, ...DS }}>
            <Link to="/" style={{ color: "rgba(255,255,255,.5)", textDecoration: "none" }}>Home</Link>
            <span style={{ color: "rgba(255,255,255,.3)" }}>›</span>
            <span style={{ color: "rgba(255,255,255,.8)" }}>Refund Policy</span>
          </nav>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(5,150,105,.2)", border: "1px solid rgba(5,150,105,.4)",
            borderRadius: 20, padding: "5px 14px", marginBottom: 16
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#34d399", display: "inline-block" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6ee7b7", letterSpacing: ".06em", textTransform: "uppercase", ...DS }}>
              Transparent & Fair
            </span>
          </div>

          <h1 style={{ fontSize: "clamp(28px,5vw,44px)", fontWeight: 900, color: "#fff", margin: "0 0 14px", lineHeight: 1.15, ...PJS }}>
            Cancellation & Refund Policy
          </h1>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,.65)", margin: "0 0 28px", maxWidth: 580, lineHeight: 1.7, ...DS }}>
            We believe in being upfront about our terms. Here's everything you need to know about cancellations, amendments, and refunds when booking with Broadway Holidays.
          </p>

          {/* Quick info cards */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { label: "Deposit", value: "From £19pp", sub: "Secures your booking" },
              { label: "Balance due", value: "10 weeks", sub: "Before departure" },
              { label: "Refund time", value: "14 days", sub: "After confirmation" },
            ].map((c, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)",
                borderRadius: 12, padding: "14px 18px", minWidth: 130,
              }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", ...DS, marginBottom: 4, textTransform: "uppercase", letterSpacing: ".05em" }}>{c.label}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", ...PJS }}>{c.value}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", ...DS }}>{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CANCELLATION TABLE ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 20px" }}>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: NAV, margin: "0 0 6px", ...PJS }}>
            Cancellation Charges at a Glance
          </h2>
          <p style={{ fontSize: 14, color: "#64748b", margin: "0 0 20px", ...DS }}>
            Charges are calculated from the date we receive your written cancellation notice.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, ...DS }}>
              <thead>
                <tr style={{ background: NAV }}>
                  {["Days Before Departure", "Cancellation Charge", "What You Lose"].map((h, i) => (
                    <th key={i} style={{
                      padding: "12px 16px", textAlign: "left", color: "#fff",
                      fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".04em"
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["More than 70 days",  "Deposit only",  "Deposit is forfeited",           "#f0fdf4", GRN],
                  ["43 – 70 days",       "50% of total",  "Half your holiday cost",          "#fff",    "#f59e0b"],
                  ["29 – 42 days",       "70% of total",  "Most of your holiday cost",       "#fff",    "#f97316"],
                  ["15 – 28 days",       "90% of total",  "Almost the full amount",          "#fff",    "#ef4444"],
                  ["14 days or fewer",   "100% of total", "No refund — full amount lost",    "#fef2f2", "#dc2626"],
                ].map(([days, charge, note, bg, color], i) => (
                  <tr key={i} style={{ background: bg, borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: NAV }}>{days}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontWeight: 800, color, fontSize: 15 }}>{charge}</span>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#64748b" }}>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 10, ...DS }}>
            * Charges may vary for group bookings of 10+ passengers or bookings with specific supplier terms. We will advise at time of booking.
          </p>
        </div>
      </div>

      {/* ── ACCORDION SECTIONS ── */}
      <div style={{ background: "#f3f6fb", padding: "40px 20px 80px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: NAV, margin: "0 0 20px", ...PJS }}>
            Full Policy Details
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {SECTIONS.map((s) => {
              const isOpen = active === s.id;
              return (
                <div key={s.id} style={{
                  background: "#fff",
                  border: `1px solid ${isOpen ? NAV + "44" : "#e2e8f0"}`,
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: isOpen ? `0 4px 20px rgba(11,31,58,.08)` : "0 1px 3px rgba(0,0,0,.03)",
                  transition: "all .2s",
                }}>
                  <button
                    onClick={() => setActive(isOpen ? null : s.id)}
                    style={{
                      width: "100%", padding: "18px 20px",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      background: "none", border: "none", cursor: "pointer", textAlign: "left",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: isOpen ? NAV : "#f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 16, flexShrink: 0, transition: "background .2s"
                      }}>{s.emoji}</span>
                      <span style={{ fontWeight: 800, fontSize: 15, color: NAV, ...PJS }}>{s.title}</span>
                    </div>
                    <span style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: isOpen ? NAV : "#f1f5f9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, transition: "all .2s",
                      color: isOpen ? "#fff" : "#64748b", fontSize: 14, fontWeight: 700,
                    }}>
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{ padding: "0 20px 20px 68px" }}>
                      <div style={{ height: 1, background: "#f1f5f9", marginBottom: 16 }} />
                      {s.content.split("\n\n").map((para, i) => (
                        <p key={i} style={{
                          fontSize: 14, color: "#475569", lineHeight: 1.8,
                          margin: "0 0 12px", ...DS,
                          whiteSpace: "pre-line"
                        }}>{para}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact CTA */}
          <div style={{
            marginTop: 32,
            background: `linear-gradient(135deg,${NAV},${NAVL})`,
            borderRadius: 16, padding: "28px 28px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 20,
          }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 900, color: "#fff", marginBottom: 6, ...PJS }}>
                Questions about your booking?
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", ...DS }}>
                Our UK team is available 7 days a week to help with cancellations, amendments, and refunds.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a href="tel:02030190055" style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "11px 18px", background: "#fff", color: NAV,
                borderRadius: 9, fontWeight: 700, fontSize: 13, textDecoration: "none", ...DS
              }}>
                📞 020 3019 0055
              </a>
              <a href="https://wa.me/447487672075?text=Hi%2C+I+have+a+question+about+my+booking"
                target="_blank" rel="noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "11px 18px", background: "#22c55e", color: "#fff",
                  borderRadius: 9, fontWeight: 700, fontSize: 13, textDecoration: "none", ...DS
                }}>
                💬 WhatsApp Us
              </a>
            </div>
          </div>

          {/* Links to other policies */}
          <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { to: "/terms", label: "📄 Terms & Conditions" },
              { to: "/privacy", label: "🔒 Privacy Policy" },
              { to: "/about", label: "🏢 About Us" },
              { to: "/contact", label: "✉️ Contact Us" },
            ].map((l) => (
              <Link key={l.to} to={l.to} style={{
                padding: "9px 16px", background: "#fff",
                border: "1px solid #e2e8f0", borderRadius: 8,
                fontSize: 13, color: NAV, textDecoration: "none", fontWeight: 600, ...DS
              }}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}